import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { api } from '../../../lib/api';

type PaymentSettings = {
  provider: string;
  testMode: boolean;
  stripePublishableKeyTest: string;
  stripeSecretKeyTest: string; // masked '***' from API
  stripeWebhookSecretTest: string; // masked '***'
  stripePublishableKeyLive: string;
  stripeSecretKeyLive: string; // masked '***'
  stripeWebhookSecretLive: string; // masked '***'
  allowedMethods: string[];
  // compatibility: active key
  stripePublishableKey?: string;
};

const PaymentSettingsPage = () => {
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState<PaymentSettings>({
    provider: 'stripe',
    testMode: false,
    stripePublishableKeyTest: '',
    stripeSecretKeyTest: '',
    stripeWebhookSecretTest: '',
    stripePublishableKeyLive: '',
    stripeSecretKeyLive: '',
    stripeWebhookSecretLive: '',
    allowedMethods: ['card', 'sepa_debit'],
  });

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get<PaymentSettings>('/api/settings/payment');
      // Ensure arrays are normalized
      const methods = Array.isArray(data?.allowedMethods)
        ? data.allowedMethods
        : ['card', 'sepa_debit'];
      setCfg({ ...data, allowedMethods: methods });
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Payment-Einstellungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleMethod = (method: 'card' | 'sepa_debit') => {
    setCfg((prev) => {
      const has = prev.allowedMethods.includes(method);
      const next = has ? prev.allowedMethods.filter((m) => m !== method) : [...prev.allowedMethods, method];
      return { ...prev, allowedMethods: next };
    });
  };

  const save = async () => {
    setLoading(true);
    try {
      // Secrets werden im Backend bei '***' beibehalten
      const payload = {
        provider: cfg.provider,
        testMode: cfg.testMode,
        stripePublishableKeyTest: cfg.stripePublishableKeyTest,
        stripeSecretKeyTest: cfg.stripeSecretKeyTest,
        stripeWebhookSecretTest: cfg.stripeWebhookSecretTest,
        stripePublishableKeyLive: cfg.stripePublishableKeyLive,
        stripeSecretKeyLive: cfg.stripeSecretKeyLive,
        stripeWebhookSecretLive: cfg.stripeWebhookSecretLive,
        allowedMethods: cfg.allowedMethods,
      };
      const updated = await api.put<PaymentSettings>('/api/settings/payment', payload);
      setCfg((prev) => ({ ...prev, ...updated }));
      toast.success('Payment-Einstellungen gespeichert');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Zahlungen (Stripe)</h1>
        <p className="text-muted-foreground">
          Test- und Live-Schlüssel verwalten, Testmodus umschalten und erlaubte Zahlungsarten wählen.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Allgemein</CardTitle>
          <CardDescription>Provider-Auswahl und Testmodus</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm">Provider</label>
            <div className="text-sm font-medium">Stripe</div>
          </div>
          <div className="flex items-center justify-between">
            <label className="text-sm">Testmodus aktiv</label>
            <Switch checked={cfg.testMode} onCheckedChange={(v) => setCfg({ ...cfg, testMode: !!v })} />
          </div>
          <div className="space-y-2">
            <div className="text-sm font-medium">Erlaubte Zahlungsarten</div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={cfg.allowedMethods.includes('card')}
                  onChange={() => toggleMethod('card')}
                />
                Karte (Stripe Elements)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={cfg.allowedMethods.includes('sepa_debit')}
                  onChange={() => toggleMethod('sepa_debit')}
                />
                SEPA-Lastschrift (IBAN)
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test-Schlüssel</CardTitle>
          <CardDescription>Verwende Stripe-Testschlüssel für die Sandbox.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2">
            <label className="text-sm">Publishable Key (Test)</label>
            <Input
              value={cfg.stripePublishableKeyTest}
              onChange={(e) => setCfg({ ...cfg, stripePublishableKeyTest: e.target.value })}
              placeholder="pk_test_..."
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm">Secret Key (Test)</label>
            <Input
              type="password"
              value={cfg.stripeSecretKeyTest}
              onChange={(e) => setCfg({ ...cfg, stripeSecretKeyTest: e.target.value })}
              placeholder="***"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm">Webhook Secret (Test)</label>
            <Input
              type="password"
              value={cfg.stripeWebhookSecretTest}
              onChange={(e) => setCfg({ ...cfg, stripeWebhookSecretTest: e.target.value })}
              placeholder="***"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Live-Schlüssel</CardTitle>
          <CardDescription>Produktivschlüssel für Live-Zahlungen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-2">
            <label className="text-sm">Publishable Key (Live)</label>
            <Input
              value={cfg.stripePublishableKeyLive}
              onChange={(e) => setCfg({ ...cfg, stripePublishableKeyLive: e.target.value })}
              placeholder="pk_live_..."
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm">Secret Key (Live)</label>
            <Input
              type="password"
              value={cfg.stripeSecretKeyLive}
              onChange={(e) => setCfg({ ...cfg, stripeSecretKeyLive: e.target.value })}
              placeholder="***"
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm">Webhook Secret (Live)</label>
            <Input
              type="password"
              value={cfg.stripeWebhookSecretLive}
              onChange={(e) => setCfg({ ...cfg, stripeWebhookSecretLive: e.target.value })}
              placeholder="***"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button disabled={loading} onClick={save}>Speichern</Button>
      </div>
    </div>
  );
};

export default PaymentSettingsPage;

